import { apiSlice } from '../../../core/api/apiSlice';

export interface CalendarConnection {
  id: string;
  provider: string;
  accountEmail: string;
  isActive: boolean;
  createdAt: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  status: string;
  prospect_email: string | null;
  meeting_url: string | null;
  start_time: string;
  end_time: string;
  participants: any[] | null;
  is_sales_call: boolean;
  has_briefing: boolean;
  external_event_id: string | null;
}

export interface ProspectBriefing {
  id: string;
  calendar_event_id: string;
  prospect_profile: {
    name: string;
    email?: string;
    role: string;
    tenure: string;
    linkedin: string;
    previous_interactions: string;
    calendar_notes?: string;
    meeting_platform?: string;
    host?: string;
    total_guests?: number;
    location?: string;
    status?: string;
  } | null;
  company_profile: {
    name: string;
    industry: string;
    size: string;
    revenue: string;
    tech_stack: string;
    recent_news: string;
  } | null;
  crm_history: {
    salesforce?: {
      pipeline_stage: string;
      deal_name?: string;
      deal_amount?: number;
      deal_close_date?: string;
      timeline: { date: string; type: string; note: string }[];
      notes: string;
    };
    hubspot?: {
      pipeline_stage: string;
      deal_name?: string;
      deal_amount?: number;
      deal_close_date?: string;
      timeline: { date: string; type: string; note: string }[];
      notes: string;
    };
  } | null;
  sentiment_analysis: {
    engagement_score: number;
    response_time_avg: string;
    meeting_participation: string;
  } | null;
  tactical_recommendations: {
    hooks: string[];
    topics_to_avoid: string[]
    communication_style: string;
  } | null;
  generated_at: string;
}

export interface CompetitiveIntel {
  id: string;
  calendar_event_id: string;
  detected_competitors: { name: string; strength: string; weakness: string }[] | null;
  dynamic_battlecards: { competitor: string; objection: string; rebuttal: string }[] | null;
  competitive_alerts: { date: string; title: string; detail: string }[] | null;
  updated_at: string;
}

export interface BriefingDetail {
  event: {
    id: string;
    title: string;
    start_time: string;
    end_time: string;
    prospect_email: string | null;
    meeting_url: string | null;
    participants: any[] | null;
    external_event_id: string;
  };
  briefing: ProspectBriefing | null;
  competitive_intel: CompetitiveIntel | null;
}

export interface ConnectionsResponse {
  success: boolean;
  message: string;
  data: CalendarConnection[];
}

export interface EventsResponse {
  success: boolean;
  message: string;
  data: CalendarEvent[];
}

export interface BriefingDetailResponse {
  success: boolean;
  message: string;
  data: BriefingDetail;
}

export interface CrmStatusResponse {
  success: boolean;
  message: string;
  data: {
    salesforce: { connected: boolean; accountEmail: string | null };
    hubspot?: { connected: boolean; accountEmail: string | null };
  };
}

export const preCallApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getConnections: builder.query<ConnectionsResponse, void>({
      query: () => '/pre-call/calendar/connections',
      providesTags: ['CalendarConnections'],
    }),

    getCalendarAuthUrl: builder.query<{ success: boolean; data: { is_mock: boolean; url: string } }, { provider: string }>({
      query: ({ provider }) => `/pre-call/calendar/auth-url?provider=${provider}`,
    }),

    connectCalendar: builder.mutation<any, { provider: string; email?: string; code?: string }>({
      query: (body) => ({
        url: '/pre-call/calendar/connect',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CalendarConnections'],
    }),

    disconnectCalendar: builder.mutation<any, string>({
      query: (connectionId) => ({
        url: `/pre-call/calendar/disconnect/${connectionId}`,
        method: 'POST',
      }),
      invalidatesTags: ['CalendarConnections'],
    }),

    syncCalendar: builder.mutation<any, void>({
      query: () => ({
        url: '/pre-call/calendar/sync',
        method: 'POST',
      }),
      invalidatesTags: ['CalendarEvents'],
    }),

    getEvents: builder.query<EventsResponse, void>({
      query: () => '/pre-call/events',
      providesTags: ['CalendarEvents'],
    }),

    getBriefingDetail: builder.mutation<BriefingDetailResponse, string>({
      query: (eventId) => ({
        url: `/pre-call/briefings/${eventId}`,
        method: 'GET',
      }),
      // Invalidate the billing balance so the credit counter refreshes in real-time
      // after the 5-credit deduction that the backend performs on this endpoint.
      // Also invalidate CalendarEvents so the event list updates its has_briefing status.
      invalidatesTags: ['BillingBalance', 'CalendarEvents'],
    }),

    regenerateBriefing: builder.mutation<BriefingDetailResponse, string>({
      query: (eventId) => ({
        url: `/pre-call/briefings/${eventId}/regenerate`,
        method: 'POST',
      }),
      invalidatesTags: ['BillingBalance', 'CalendarEvents'],
    }),

    // ── CRM / Salesforce ──
    getCrmStatus: builder.query<CrmStatusResponse, void>({
      query: () => '/pre-call/crm/status',
      providesTags: ['CrmConnections'],
    }),

    getCrmAuthUrl: builder.query<{ success: boolean; data: { url: string } }, { provider: string; state?: string }>({
      query: ({ provider, state }) => `/pre-call/crm/auth-url/${provider}${state ? `?state=${encodeURIComponent(state)}` : ''}`,
    }),

    disconnectCrm: builder.mutation<any, string>({
      query: (provider) => ({
        url: `/pre-call/crm/disconnect/${provider}`,
        method: 'POST',
      }),
      invalidatesTags: ['CrmConnections'],
    }),
  }),
});

export const {
  useGetConnectionsQuery,
  useGetCalendarAuthUrlQuery,
  useLazyGetCalendarAuthUrlQuery,
  useConnectCalendarMutation,
  useDisconnectCalendarMutation,
  useSyncCalendarMutation,
  useGetEventsQuery,
  useGetBriefingDetailMutation,
  useRegenerateBriefingMutation,
  useGetCrmStatusQuery,
  useLazyGetCrmAuthUrlQuery,
  useDisconnectCrmMutation,
} = preCallApi;
